import { useState } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, Plus } from 'lucide-react';
import { useT } from '../i18n';
import { api } from '../api/client';
import { getClosedColumnIds } from '../utils/stages';
import type { GridColumn } from '../types';
import { ConfirmModal } from './ConfirmModal';
import { BareTextarea, formatMilestoneDueDate, type Milestone, type Deliverable } from './ProjectForm';
import { CdeMilestoneLink } from './CdeMilestoneLink';
import { DateHistory } from './DateHistory';

export function newMilestone(): Milestone {
  return {
    id: 'm' + Math.random().toString(36).slice(2, 9),
    name: '', contents: '', projectedScope: '', status: 'pending',
    estimatedStart: '', estimatedEnd: '', assignments: [], acceptance: '', acceptanceDeliverables: [],
    dueDateHistory: [], startDateHistory: [],
  };
}

// One draggable milestone card (Planning).
function SortableMilestone({ m, index, projectId, canEdit, collaborators, deliverables, onChange, onRemove }: {
  m: Milestone;
  index: number;
  projectId?: number;
  canEdit: boolean;
  collaborators: string[];
  deliverables: Deliverable[];
  onChange: (m: Milestone) => void;
  onRemove?: () => void; // none: a card on its own (no reordering or removing)
}) {
  const { t } = useT();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: m.id, disabled: !canEdit });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const upd = (patch: Partial<Milestone>) => onChange({ ...m, ...patch });

  const available = collaborators.filter(c => c.trim() && !m.assignments.some(a => a.name.trim().toLowerCase() === c.trim().toLowerCase()));
  const addAssign = (name: string) => upd({ assignments: [...m.assignments, { name, note: '' }] });
  const setAssignNote = (i: number, note: string) => upd({ assignments: m.assignments.map((a, j) => j === i ? { ...a, note } : a) });
  const removeAssign = (i: number) => upd({ assignments: m.assignments.filter((_, j) => j !== i) });
  const toggleDeliv = (id: string) => upd({ acceptanceDeliverables: m.acceptanceDeliverables.includes(id) ? m.acceptanceDeliverables.filter(x => x !== id) : [...m.acceptanceDeliverables, id] });

  // A cancelled milestone keeps its place (and number), greyed and read-only.
  // Cancelled here but not saved yet (no record), it can still be put back.
  if (m.status === 'cancelled') {
    const c = m.cancellation;
    return (
      <div ref={setNodeRef} style={style} className="ms-card ms-card--cancelled" data-live-anchor={`msc-${m.id}`}>
        <div className="ms-card-head">
          {canEdit && (
            <button type="button" className="ms-drag" title={t('milestones.dragReorder')} {...attributes} {...listeners}>
              <GripVertical size={15} />
            </button>
          )}
          <span className="ms-card-tag">{t('milestones.milestoneN', { n: index + 1 })}</span>
          <span className="ms-cancelled-name">{m.name || t('milestones.untitled')}</span>
          <span className="badge badge-plain">{t('vjt.cancelled.badge')}</span>
          {canEdit && !c && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => upd({ status: m.statusBeforeCancel || 'pending', statusBeforeCancel: undefined })}>
              {t('milestones.undoCancel')}
            </button>
          )}
        </div>
        <div className="ms-cancelled-info">
          {c?.at ? t('vjt.cancelled.by', { date: formatMilestoneDueDate(c.at.slice(0, 10)), name: c.by || '—' }) : t('milestones.cancelOnSave')}
          {c?.reason && <> — {t('vjt.cancelled.reason', { reason: c.reason })}</>}
        </div>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} className="ms-card" data-live-anchor={`msc-${m.id}`}>
      <div className="ms-field"><label className="pq-sub-label">{t('cde.milestoneType')}</label><select className="form-select" disabled={!canEdit} value={m.type || 'standard'} onChange={e=>upd({type:e.target.value as 'standard'|'process'})}><option value="standard">{t('cde.standard')}</option><option value="process">{t('cde.process')}</option></select>{m.type==='process' && projectId && <CdeMilestoneLink projectId={projectId} milestoneId={m.id} canEdit={canEdit}/>}</div>
      <div className="ms-card-head">
        {canEdit && onRemove && (
          <button type="button" className="ms-drag" title={t('milestones.dragReorder')} {...attributes} {...listeners}>
            <GripVertical size={15} />
          </button>
        )}
        <span className="ms-card-tag">{t('milestones.milestoneN', { n: index + 1 })}</span>
        {canEdit && onRemove && (
          <button type="button" className="ms-remove" title={t('milestones.removeMilestone')} onClick={onRemove}>
            <X size={14} />
          </button>
        )}
      </div>

      <div className="ms-field">
        <label className="pq-sub-label">{t('common.title')}</label>
        {canEdit
          ? <input className="form-input--bare" value={m.name} placeholder={t('milestones.titlePlaceholder')} onChange={(e) => upd({ name: e.target.value })} />
          : <div className="form-input--bare" style={{ cursor: 'default' }}>{m.name || '—'}</div>}
      </div>

      <div className="ms-field">
        <label className="pq-sub-label">{t('common.description')}</label>
        {canEdit
          ? <BareTextarea value={m.contents} placeholder={t('milestones.descriptionPlaceholder')} onChange={(v) => upd({ contents: v })} />
          : <div className="form-input--bare" style={{ cursor: 'default', whiteSpace: 'pre-wrap' }}>{m.contents || '—'}</div>}
      </div>

      <div className="ms-field">
        <label className="pq-sub-label">{t('milestones.estimatedStart')}</label>
        {canEdit
          ? <input className="form-input--bare" type="date" value={m.estimatedStart} onChange={(e) => upd({ estimatedStart: e.target.value })} />
          : <div className="form-input--bare" style={{ cursor: 'default' }}>{m.estimatedStart || '—'}</div>}
        <DateHistory history={m.startDateHistory} />
      </div>

      <div className="ms-field">
        <label className="pq-sub-label">{t('milestones.estimatedEnd')}</label>
        {canEdit
          ? <input className="form-input--bare" type="date" value={m.estimatedEnd} onChange={(e) => upd({ estimatedEnd: e.target.value })} />
          : <div className="form-input--bare" style={{ cursor: 'default' }}>{m.estimatedEnd || '—'}</div>}
        <DateHistory history={m.dueDateHistory} />
      </div>

      <div className="ms-field">
        <label className="pq-sub-label">{t('milestones.whoDoesWhat')}</label>
        {m.assignments.length > 0 && m.assignments.map((a, ai) => (
          <div className="ms-assign" key={ai}>
            <span className="ms-assign-name">{a.name}</span>
            {canEdit
              ? <BareTextarea value={a.note} placeholder={t('milestones.assignPlaceholder', { name: a.name })} onChange={(v) => setAssignNote(ai, v)} />
              : <span className="text-sm text-ink-2" style={{ whiteSpace: 'pre-wrap' }}>{a.note || '—'}</span>}
            {canEdit && <button type="button" className="tag-remove" title={t('milestones.unassign')} onClick={() => removeAssign(ai)}><X size={12} /></button>}
          </div>
        ))}
        {canEdit && available.length > 0 && (
          <div className="ms-assign-add">
            <span className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('milestones.assign')}</span>
            {available.map(c => <button key={c} type="button" className="ms-chip" onClick={() => addAssign(c)}>+ {c}</button>)}
          </div>
        )}
        {m.assignments.length === 0 && (!canEdit || available.length === 0) && (
          <div className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('milestones.noneAssigned')}</div>
        )}
      </div>

      <div className="ms-field">
        <label className="pq-sub-label">{t('milestones.acceptance')}</label>
        {canEdit
          ? <BareTextarea value={m.acceptance} placeholder={t('milestones.acceptancePlaceholder')} onChange={(v) => upd({ acceptance: v })} />
          : <div className="form-input--bare" style={{ cursor: 'default', whiteSpace: 'pre-wrap' }}>{m.acceptance || '—'}</div>}
        {deliverables.length > 0 && (
          <div className="ms-deliv">
            <span className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('milestones.linkedDeliverables')}</span>
            <div className="ms-deliv-chips">
              {deliverables.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={`ms-chip${m.acceptanceDeliverables.includes(d.id) ? ' on' : ''}`}
                  disabled={!canEdit}
                  onClick={() => canEdit && toggleDeliv(d.id)}
                >{d.title || t('milestones.untitled')}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** One milestone's Planning card on its own — the same fields as in the Planning * tab, e.g. to add a milestone from the Execution tab. */export function MilestoneCard({ m, index, projectId, collaborators, deliverables, onChange }: {  m: Milestone;  index: number;  projectId?: number;  collaborators: string[];  deliverables: Deliverable[];  onChange: (m: Milestone) => void;}) {  return (    <DndContext>      <SortableContext items={[m.id]}>        <SortableMilestone m={m} index={index} projectId={projectId} canEdit collaborators={collaborators} deliverables={deliverables} onChange={onChange} />      </SortableContext>    </DndContext>  );}
export function MilestonesEditor({ projectId, milestones, onChange, collaborators, deliverables, canEdit }: {
  projectId?: number;
  milestones: Milestone[];
  onChange: (m: Milestone[]) => void;
  collaborators: string[];
  deliverables: Deliverable[];
  canEdit: boolean;
}) {
  const { t } = useT();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      const oldIdx = milestones.findIndex(m => m.id === active.id);
      const newIdx = milestones.findIndex(m => m.id === over.id);
      if (oldIdx >= 0 && newIdx >= 0) onChange(arrayMove(milestones, oldIdx, newIdx));
    }
  };

  // Cancelling a milestone asks first, with how many open tasks are still linked
  // to it (they'll be archived when the project is saved).
  const [pendingRemove, setPendingRemove] = useState<{ id: string; openTasks: number } | null>(null);
  const askRemove = async (id: string) => {
    let openTasks = 0;
    if (projectId) {
      try {
        const [tasks, cols] = await Promise.all([
          api.getProjectTasks(projectId) as Promise<{ milestone_id: string | null; column_id: number }[]>,
          api.getKanbanColumns() as Promise<GridColumn[]>,
        ]);
        const closed = getClosedColumnIds(cols);
        openTasks = tasks.filter(x => x.milestone_id === id && !closed.has(x.column_id)).length;
      } catch { /* the confirmation still shows, without the count */ }
    }
    setPendingRemove({ id, openTasks });
  };

  const cards = milestones.map((m, i) => (
    <SortableMilestone
      key={m.id} m={m} index={i} projectId={projectId} canEdit={canEdit} collaborators={collaborators} deliverables={deliverables}
      onChange={(nm) => onChange(milestones.map((x, j) => j === i ? nm : x))}
      onRemove={() => askRemove(m.id)}
    />
  ));

  return (
    <div>
      {milestones.length === 0 && <div className="text-sm text-ink-3" style={{ fontStyle: 'italic', marginBottom: 'var(--s8)' }}>{t('milestones.empty')}</div>}
      {canEdit ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={milestones.map(m => m.id)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-12">{cards}</div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="flex flex-col gap-12">{cards}</div>
      )}
      {canEdit && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 'var(--s12)' }} onClick={() => onChange([...milestones, newMilestone()])}>
          <Plus className="icon-sm" /> {t('milestones.addMilestone')}
        </button>
      )}
      <ConfirmModal
        open={pendingRemove !== null}
        title={t('milestones.removeConfirmTitle')}
        message={pendingRemove?.openTasks
          ? t('milestones.removeConfirmTasks', { n: pendingRemove.openTasks })
          : t(projectId ? 'milestones.removeConfirm' : 'milestones.removeConfirmNew')}
        destructive
        onConfirm={() => {
          const id = pendingRemove?.id;
          setPendingRemove(null);
          // On a saved project the milestone is cancelled in its place (recorded on save);
          // a project not created yet has nothing to keep a trace of.
          if (id) onChange(projectId
            ? milestones.map(x => x.id === id ? { ...x, statusBeforeCancel: x.status, status: 'cancelled', cancellation: null } : x)
            : milestones.filter(x => x.id !== id));
        }}
        onCancel={() => setPendingRemove(null)}
      />
    </div>
  );
}
